import React, { useState } from 'react';
import {
  useLocation,
  Navigate
} from 'react-router';
import axios from 'axios';
import { makeStyles } from '@mui/styles';
import {
  List,
  ListItem,
  CircularProgress,
  Fade,
  Box,
  Grid,
  Hidden,
  TextField,
} from '@mui/material';
import PAGES from 'common/pages';
import { VITE_PORTAL_API } from 'config';
import API from 'common/api';
import SnackMessage from 'components/SnackMessage/SnackMessage';
import { useSnackbar } from 'notistack';
import { ArrowForward, ArrowBack } from '@mui/icons-material';
import useAuth from 'hooks/useAuth';
import {
  Button, Card, Typography, RouterLink, IconButton
} from 'bso-ui';
import TransitionHOC from 'components/TransitionHOC/TransitionHOC';

const useStyles = makeStyles((theme) => ({
  form: {
    width: '100%',
    padding: theme.spacing(3)
  },
  title: {
    textAlign: 'center',
    alignItems: 'center',
  },
  subtitle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(5),
    // paddingLeft: theme.spacing(3),
    // paddingRight: theme.spacing(3)
  },
  submitButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

const initialFormState = {
  email: '',
  first_name: '',
  last_name: '',
  password: '',
  confirm_password: '',
  accept: false
};

const initialFormErrorState = {
  email: '',
  first_name: '',
  last_name: '',
  password: '',
  confirm_password: '',
};

const ResendConfirmSignUp = () => {
  const classes = useStyles();
  const location = useLocation();
  const { isSignedIn } = useAuth();
  const { from } = location.state || { from: { pathname: '/' } };
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [formState, setFormState] = useState(initialFormState);
  const [formErrorState, setFormErrorState] = useState(initialFormErrorState);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);

  const handleChange = (name, value) => {
    setFormState({ ...formState, [name]: value });
  };

  const isError = (error) => Boolean(error);

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);

    const url = `${VITE_PORTAL_API}${API.RESEND_CONFIRM_SIGN_UP}`;
    axios
      .post(
        url,
        { ...formState }
      )
      .then(() => {
        setLoading(false);
        setIsSubmitSuccess(true);
      })
      .catch((err) => {
        setLoading(false);
        setIsSubmitSuccess(false);
        setFormErrorState(err.response.data);
        if (err.response && err.response.data.error) {
          enqueueSnackbar(err.response.data.error, {
            content: (key) => (
              <SnackMessage
                data-testid="error-msg"
                id={key}
                message={err.response.data.error}
                variant="error"
              />
            )
          });
        } else {
          enqueueSnackbar(err.toString(), {
            content: (key) => (
              <SnackMessage
                data-testid="error-msg"
                id={key}
                message={err.toString()}
                variant="error"
              />
            )
          });
        }
      });
  };

  if (isSignedIn()) {
    // return <Redirect to={from} />;
    return <Navigate replace to={from} />;
  }

  if (isSubmitSuccess) {
    return (
      <div>
        <Fade
          in
          mountOnEnter
          timeout={400}
          unmountOnExit
        >
          <div>
            <Card color="gradient">
              <Box p={3}>
                <Card color="white">
                  <div className={classes.form}>
                    <Typography
                      className={classes.title}
                      data-testid="welcome-message"
                      variant="h3"
                    >
                      A confirmation link has been sent to your email.
                    </Typography>
                    <div className={classes.subtitle}>
                      <Typography
                        color="textSecondary"
                        variant="subtitle1"
                      >
                        To complete sign up, please confirm using the link we sent to your email.
                      </Typography>

                    </div>
                    <div className={classes.subtitle}>
                      <RouterLink to={{ pathname: PAGES.SIGN_IN, state: { slideTransition: 'left' } }}>
                        <Button
                          endIcon={<ArrowForward />}
                          variant="cta"
                          state="outlined"
                          color="primary"
                        >
                          Sign in
                        </Button>
                      </RouterLink>
                    </div>
                  </div>
                </Card>
              </Box>
            </Card>
          </div>
        </Fade>
      </div>

    );
  }

  return (
    <div>
      <TransitionHOC
        in
        mountOnEnter
        timeout={400}
        unmountOnExit
      >
        <Box>
          <Card color="gradient">
            <Box p={2}>
              <Grid container>
                <Hidden smDown>
                  <Grid item md={6}>
                    <Box display="flex" flexDirection="column" alignItems="flex-start" paddingBottom={4}>
                      <Typography
                        className={classes.title}
                        data-testid="welcome-message"
                        variant="h1"
                      >
                        Join our
                      </Typography>
                      <Typography
                        data-testid="welcome-message"
                        variant="h1"
                      >
                        portal_
                      </Typography>
                    </Box>
                  </Grid>
                </Hidden>
                <Grid item md={6} xs={12}>
                  <Card color="white">
                    <form
                      className={classes.form}
                      onSubmit={handleSubmit}
                    >
                      <Box display="flex">
                        <Box>
                          <RouterLink to={{ pathname: PAGES.SIGN_IN, state: { slideTransition: 'left' } }}>
                            <IconButton color="white">
                              <Box>
                                <ArrowBack />
                              </Box>
                            </IconButton>
                          </RouterLink>
                        </Box>
                        <RouterLink to={{ pathname: PAGES.SIGN_IN, state: { slideTransition: 'left' } }}>
                          <Box marginTop={0.7}>
                            <Typography variant="button" color="primary">
                              Back to sign in
                            </Typography>
                          </Box>
                        </RouterLink>
                      </Box>
                      <div className={classes.subtitle}>
                        <Typography
                          variant="h3"
                        >
                          Enter your email below and we&apos;ll resend the sign-up confirmation link
                        </Typography>
                      </div>
                      <List disablePadding>
                        <ListItem disableGutters>
                          {/* <FormField */}
                          <TextField
                            data-testid="email-input"
                            label="Email address"
                            name="email"
                            onChange={(e) => handleChange(e.target.name, e.target.value)}
                            type="string"
                            variant="outlined"
                            fullWidth
                            value={formState.email}
                            error={isError(formErrorState.email)}
                            helperText={formErrorState.email ? formErrorState.email : null}
                          />
                        </ListItem>
                      </List>
                      <Box marginTop={3}>
                        <Button
                          className={classes.submitButton}
                          data-testid="ResendConfirmSignUp-button"
                          disabled={!formState.email || loading}
                          endIcon={!loading ? <ArrowForward /> : <CircularProgress size={20} />}
                          type="submit"
                          variant="cta"
                          state="outlined"
                          color="primary"
                        >
                          Resend link
                        </Button>
                      </Box>
                    </form>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Card>
        </Box>
      </TransitionHOC>
    </div>
  );

  // return (
  //   <div>
  //     <Fade
  //       in
  //       mountOnEnter
  //       timeout={400}
  //       unmountOnExit
  //     >
  //       <div>
  //         <form
  //           className={classes.form}
  //           onSubmit={handleSubmit}
  //         >
  //           <Typography
  //             className={classes.title}
  //             data-testid="welcome-message"
  //             variant="h1"
  //           >
  //             Request Confirmation Link
  //           </Typography>
  //           <div className={classes.subtitle} />
  // <List disablePadding>
  //   <ListItem disableGutters>
  //     <FormFieldFactory
  //       data-testid="email-input"
  //       label="Email Address"
  //       name="email"
  //       onChange={handleChange}
  //       type="string"
  //       value={formState.email}
  //       error={isError(formErrorState.email)}
  //       helperText={formErrorState.email ? formErrorState.email : null}
  //     />
  //   </ListItem>
  // </List>
  // <Button
  //   className={classes.submitButton}
  //   color="primary"
  //   data-testid="ResendConfirmSignUp-button"
  //   disabled={!formState.email || loading}
  //   endIcon={!loading ? <ArrowForward /> : <CircularProgress size={20} />}
  //   fullWidth
  //   size="large"
  //   type="submit"
  //   variant="contained"
  // >
  //   Resend Link
  // </Button>
  //         </form>

  //       </div>
  //     </Fade>
  //   </div>
  // );
};

export default ResendConfirmSignUp;
